import type { ComponentType } from 'react';
import { toast } from 'react-toastify';
import { Button } from '@accure/ui';
import { Menu } from '@headlessui/react';
import { ArrowDownTrayIcon, PaperClipIcon } from '@heroicons/react/24/outline';
import saveAs from 'file-saver';
import {
  AlertCircle,
  ChevronDown,
  File,
  FileImage,
  Loader2,
  Trash2,
  User,
  Users,
} from 'lucide-react';
import { useBoolean } from 'usehooks-ts';

import type { CommunicationAttachment } from '@/api/fe-api/entities/communication';
import { DeleteEmail } from '@/api/fe-api/view/mutations/delete-email';
import { DownloadCommunicationAttachment } from '@/api/fe-api/view/mutations/download-communication-attachment';
import { GetOneEmailCommunication } from '@/api/fe-api/view/queries/get-one-email-communication';
import { formatDatetime } from '@/utils/format/date';

import { useDetailsId } from '../hooks/use-details-id';

import { CommunicationDetailsBox } from './communication-details-box';
import { CommunicationDetailsHeader } from './communication-details-header';
import { CommunicationDetailsLinkedSites } from './communication-details-linked-sites';
import { CommunicationDetailsParticipant } from './communication-details-participant';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Skeleton } from './ui/skeleton';

export type CommunicationDetailsEmailProps = {
  id: string;
};

export function CommunicationDetailsEmail({
  id,
}: CommunicationDetailsEmailProps) {
  const [, setDetailsId] = useDetailsId();
  const downloadAttachment = DownloadCommunicationAttachment.useMutation();
  const emailQuery = GetOneEmailCommunication.useQuery({ id });
  const deleteEmail = DeleteEmail.useMutation();
  const showDeleteConfirm = useBoolean(false);

  async function handleDownloadAttachment(attachment: CommunicationAttachment) {
    const download = downloadAttachment.mutateAsync({ id: attachment.id });

    const blob = await toast.promise(download, {
      pending: 'Downloading the attachment...',
      success: undefined,
      error: 'Failed to download the attachment. Please try again.',
    });

    saveAs(blob, attachment.name);
  }

  if (emailQuery.isLoading) {
    return <LoadingSkeleton />;
  }

  if (emailQuery.isError) {
    throw emailQuery.error;
  }

  if (!emailQuery.data) {
    throw new Error('No data');
  }

  const email = emailQuery.data;
  async function onDelete() {
    await deleteEmail.mutateAsync(email);
    showDeleteConfirm.setFalse();
    setDetailsId(undefined);
  }

  return (
    <>
      <Dialog
        open={showDeleteConfirm.value}
        onOpenChange={showDeleteConfirm.setValue}
      >
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="size-5" />
              Confirm Deletion
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete these email? This action cannot be
              undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="sm:justify-start">
            <Button
              variant="destructive"
              onClick={onDelete}
              type="button"
              disabled={deleteEmail.isLoading}
            >
              {deleteEmail.isLoading ? (
                <>
                  <Loader2 className="mr-2 size-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </Button>
            <Button
              variant="outline"
              onClick={showDeleteConfirm.setFalse}
              type="button"
              disabled={deleteEmail.isLoading}
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="max-w-7xl space-y-6 font-sans">
        <CommunicationDetailsHeader
          title={email.subject}
          subtitle={formatDatetime(email.date)}
          actions={
            <Menu as="div" className="relative inline-block text-left">
              <Menu.Button className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none">
                <span>Actions</span>
                <div className="mx-1.5 h-3.5 w-px bg-gray-300"></div>
                <ChevronDown className="size-3.5" />
              </Menu.Button>
              <Menu.Items className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-none">
                <div className="py-1">
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={showDeleteConfirm.setTrue}
                        className={`${
                          active ? 'bg-gray-100 text-red-600' : 'text-red-600'
                        } flex w-full items-center px-4 py-2 text-sm`}
                      >
                        <Trash2 className="mr-2 size-4" />
                        Delete Email
                      </button>
                    )}
                  </Menu.Item>
                </div>
              </Menu.Items>
            </Menu>
          }
        />

        <CommunicationDetailsBox className="space-y-4">
          <div className="flex items-center gap-4">
            <User className="size-5 text-gray-600" />
            <div>
              <div className="text-xs">From</div>
              <div className="text-sm">
                <CommunicationDetailsParticipant participant={email.sender} />
              </div>
            </div>
          </div>

          {(email.recipients && email.recipients.length > 0) ||
          (email.cc && email.cc.length > 0) ? (
            <div className="flex items-start gap-4">
              <Users className="size-5 text-gray-600" />
              <div className="flex-1 space-y-3">
                {email.recipients.length > 0 ? (
                  <div>
                    <div className="text-xs">To</div>
                    <div className="space-y-1 text-sm">
                      {email.recipients.map((recipient, index) => (
                        <div key={index}>
                          <CommunicationDetailsParticipant
                            participant={recipient}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}

                {email.cc.length > 0 ? (
                  <div>
                    <div className="text-xs">CC</div>
                    <div className="space-y-1 text-sm">
                      {email.cc.map((recipient, index) => (
                        <CommunicationDetailsParticipant
                          key={index}
                          participant={recipient}
                        />
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          ) : null}
        </CommunicationDetailsBox>

        <CommunicationDetailsBox>
          <CommunicationDetailsLinkedSites communication={email} />
        </CommunicationDetailsBox>

        <CommunicationDetailsBox>
          {email.body.trim() === '' ? (
            <div className="text-center text-sm italic text-gray-800/20">
              empty body
            </div>
          ) : (
            <div className="prose prose-sm max-w-none overflow-x-auto whitespace-pre-wrap font-normal text-gray-800">
              {email.body}
            </div>
          )}
        </CommunicationDetailsBox>

        {email.attachments.length > 0 ? (
          <CommunicationDetailsBox className="space-y-3">
            <h3 className="flex items-center text-sm font-medium text-gray-900">
              <PaperClipIcon className="mr-1 size-5 text-gray-700" />
              Attachments ({email.attachments.length})
            </h3>
            <ul className="divide-y divide-gray-200">
              {email.attachments.map(function (attachment) {
                const Icon = attachmentIcon(attachment);

                return (
                  <li
                    key={attachment.id}
                    className="flex items-center justify-between py-2"
                  >
                    <div className="flex items-center gap-2">
                      <Icon className="size-5 text-gray-600" />

                      <span className="text-sm text-gray-800">
                        {attachment.name}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleDownloadAttachment(attachment)}
                      className="flex items-center text-sm text-gray-700 hover:text-gray-900"
                    >
                      <ArrowDownTrayIcon className="mr-1 size-4" />
                      Download
                    </button>
                  </li>
                );
              })}
            </ul>
          </CommunicationDetailsBox>
        ) : null}
      </div>
    </>
  );
}

function attachmentIcon(
  attachment: CommunicationAttachment,
): ComponentType<{ className?: string }> {
  switch (attachment.contentType) {
    case 'image/png':
    case 'image/jpg':
    case 'image/jpeg':
    case 'image/gif': {
      return FileImage;
    }
    default: {
      return File;
    }
  }
}

function LoadingSkeleton() {
  return (
    <div className="max-w-7xl space-y-6">
      <div className="rounded-lg p-4">
        <Skeleton className="mb-2 h-8 w-3/4" />
        <Skeleton className="h-4 w-1/3" />
      </div>

      <div className="space-y-4 rounded-lg border bg-card p-4">
        <div className="flex items-center">
          <Skeleton className="mr-2 size-5 rounded-full" />
          <div>
            <Skeleton className="mb-1 h-3 w-16" />
            <Skeleton className="h-4 w-40" />
          </div>
        </div>

        <div className="flex items-start">
          <Skeleton className="mr-2 size-5 rounded-full" />
          <div className="flex-1">
            <Skeleton className="mb-1 h-3 w-16" />
            <div className="space-y-1">
              <Skeleton className="h-4 w-64" />
              <Skeleton className="h-4 w-56" />
            </div>

            <Skeleton className="mb-1 mt-3 h-3 w-16" />
            <div className="space-y-1">
              <Skeleton className="h-4 w-60" />
              <Skeleton className="h-4 w-52" />
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="space-y-2">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-4 w-5/6" />
          <Skeleton className="h-4 w-2/3" />
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <Skeleton className="mb-3 h-5 w-40" />
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Skeleton className="mr-2 size-5 rounded" />
              <Skeleton className="h-4 w-48" />
              <Skeleton className="ml-2 h-3 w-16" />
            </div>
            <Skeleton className="h-4 w-20" />
          </div>
        </div>
      </div>
    </div>
  );
}
